import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../i18n';
import { useEmail } from '../../context/EmailContext';
import emailService from '../../services/emailService';
import { EmailListSkeleton } from './EmailSkeleton';
import { FaStar, FaPaperclip } from 'react-icons/fa';

const EmailList = ({ selectedIds, setSelectedIds, searchQuery, setSearchQuery, mobileView, navigateToView }) => {
    const { t } = useI18n();
    const {
        emails,
        activeEmailId,
        activeAccountId,
        selectEmail,
        isLoading,
        pagination,
        loadMore,
    } = useEmail();

    const [searchResults, setSearchResults] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    const toggleSelect = (e, emailId) => {
        e.stopPropagation();
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(emailId) ? next.delete(emailId) : next.add(emailId);
            return next;
        });
    };

    // Debounced server-side search with AbortController to cancel in-flight requests
    // M1: Prevents setState after unmount and avoids stale search results
    useEffect(() => {
        if (searchQuery.length < 2) {
            setSearchResults(null);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            if (!activeAccountId) return;
            setIsSearching(true);
            try {
                const response = await emailService.searchWithSignal(activeAccountId, searchQuery, controller.signal);
                if (!controller.signal.aborted) {
                    setSearchResults(response?.data?.emails || []);
                }
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError' || err?.name === 'CanceledError') return;
                console.error('Search failed', err);
                if (!controller.signal.aborted) {
                    setSearchResults([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsSearching(false);
                }
            }
        }, 500);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [searchQuery, activeAccountId]);

    // Client-side filter for short queries (< 2 chars)
    const clientFiltered = searchQuery
        ? emails.filter(email =>
            email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.from?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.from?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.snippet?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : emails;

    // What to actually show
    const displayEmails = searchQuery.length >= 2 ? (searchResults ?? clientFiltered) : clientFiltered;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        const isThisYear = date.getFullYear() === now.getFullYear();
        if (isThisYear) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }

        return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getStatusBadge = (email) => {
        if (email.status === 'pending') {
            return <span className="ui-font-10" style={{ background: '#f59e0b', color: '#fff', borderRadius: '3px', padding: '1px 4px', marginLeft: '4px' }}>{t('pages.email.list.sending')}</span>;
        }
        if (email.status === 'failed') {
            return <span className="ui-font-10" style={{ background: '#ef4444', color: '#fff', borderRadius: '3px', padding: '1px 4px', marginLeft: '4px' }}>{t('pages.email.list.failed')}</span>;
        }
        return null;
    };

    return (
        <div className="email-list-panel">
            <div className="email-list">
                {isLoading && !isSearching && displayEmails.length === 0 ? (
                    <EmailListSkeleton count={7} />
                ) : isSearching && displayEmails.length === 0 ? (
                    <div className="email-loading">{t('pages.email.list.searching')}</div>
                ) : displayEmails.length === 0 ? (
                    <div className="email-loading">
                        {searchQuery ? t('pages.email.list.no_match') : t('pages.email.list.no_emails')}
                    </div>
                ) : (
                    <>
                        {displayEmails.map(email => (
                            <div
                                key={email.id}
                                className={`email-list-item ${!email.is_read ? 'unread' : ''} ${email.id === activeEmailId ? 'active' : ''} ${selectedIds.has(email.id) ? 'selected' : ''}`}
                                onClick={() => {
                                    selectEmail(email.id);
                                    if (navigateToView) navigateToView('preview');
                                }}
                            >
                                <div className="email-item-header">
                                    <div className="email-item-checkbox-wrap" onClick={(e) => toggleSelect(e, email.id)}>
                                        <input
                                            type="checkbox"
                                            className="email-item-checkbox"
                                            checked={selectedIds.has(email.id)}
                                            onChange={() => {}}
                                        />
                                    </div>
                                    <span className="email-item-from">
                                        {email.from?.name || email.from?.email || 'Unknown'}
                                        {getStatusBadge(email)}
                                    </span>
                                    <div className="email-item-icons">
                                        {email.is_starred && (
                                            <FaStar className="email-item-star" />
                                        )}
                                        {email.has_attachments && (
                                            <FaPaperclip className="email-item-attachment" />
                                        )}
                                    </div>
                                    <span className="email-item-date">{formatDate(email.date)}</span>
                                </div>
                                <div className="email-item-subject">
                                    {email.subject || t('pages.email.list.no_subject')}
                                </div>
                                <div className="email-item-snippet">
                                    {email.snippet || ''}
                                </div>
                            </div>
                        ))}

                        {!searchQuery && pagination.hasMore && (
                            <button
                                className="email-load-more-btn"
                                onClick={loadMore}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Loading...' : 'Load more'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailList;
